import './App.css';
import './QuoteWizard.css';
import { useEffect, useState } from 'react';
import QuoteWizard from './QuoteWizard';

// High-resolution stock images from Unsplash
const IMAGES = {
  hero: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=1600&q=80",
  residential: "https://images.unsplash.com/photo-1527515637462-cff94eecc1ac?auto=format&fit=crop&w=800&q=80",
  commercial: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&q=80",
  deep: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=800&q=80",
  airbnb: "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=800&q=80"
};

const logoUrl = `${process.env.PUBLIC_URL}/Clenzcleanlogo.jpeg`;

// --- Main App Component ---
function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  const handleStartQuote = (service) => {
    if (service === 'commercial') {
      setSelectedService('commercial');
    } else {
      setSelectedService('residential');
    }
    const quoteSection = document.getElementById('quote');
    if (quoteSection) {
      quoteSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 24);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          if (el.matches(".io-reveal")) el.classList.add("in-view");
          observer.unobserve(el);
        });
      },
      { threshold: 0.15 }
    );

    document.querySelectorAll(".io-reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsSheetOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  useEffect(() => {
    if (isSheetOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isSheetOpen]);

  return (
    <div className="App">
      <a className="skip-link" href="#main">Skip to content</a>

      {/* Header */}
      <header className={`nav ${isScrolled ? 'is-scrolled' : ''}`}>
        <div className="nav__inner">
          <a className="nav__logo" href="#top">
            <img src={logoUrl} alt="Clenz Clean Logo" className="nav__logo-img" />
          </a>
          <nav className="nav__links">
            <a href="#services">Services</a>
            <a href="#standard">Why Us</a>
            <a href="#reviews">Reviews</a>
            <a href="#areas">Areas</a>
          </nav>
          <div className="nav__meta">
            <a className="nav__phone" href="tel:+447849040289">+44 7849 040289</a>
            <a className="btn btn--primary" href="#quote">Get a Quote</a>
          </div>
          <button 
            className="nav__burger" 
            onClick={() => setIsSheetOpen(true)}
            aria-label="Open menu"
          >
            <span></span><span></span><span></span>
          </button>
        </div>
      </header>

      {/* Mobile Menu Drawer */}
      {isSheetOpen && (
        <div className="sheet-overlay" onClick={() => setIsSheetOpen(false)}>
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            <div className="sheet__inner">
              <div className="sheet__top">
                <img src={logoUrl} alt="Clenz Clean Logo" className="sheet__logo-img" />
                <button className="sheet__close" onClick={() => setIsSheetOpen(false)}>✕</button>
              </div>
              <nav className="sheet__links" onClick={() => setIsSheetOpen(false)}>
                <a href="#services">Services</a>
                <a href="#standard">Why Us</a>
                <a href="#reviews">Reviews</a>
                <a href="#areas">Areas</a>
              </nav>
              <div className="sheet__foot">
                <a className="sheet__phone" href="tel:+447849040289">+44 7849 040289</a>
                <a className="btn btn--primary" href="#quote">Get a Quote</a>
              </div>
            </div>
          </div>
        </div>
      )}

      <main id="main">
        {/* Hero Section */}
        <section className="hero" id="top" style={{ backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.78), rgba(15, 23, 42, 0.9)), url(${IMAGES.hero})` }}>
          <div className="hero__inner">
            <h1 className="hero__title">
              Professional Cleaning <br />
              <span className="hero__highlight">Across London & Essex</span>
            </h1>
            <p className="hero__subtitle">
              Build your quote in 60 seconds. Select your property type, choose your services, and see the price instantly — no waiting for callbacks. We'll confirm your booking within 24 hours.
            </p>
            <div className="hero__actions">
              <button className="btn btn--primary btn--large" onClick={() => handleStartQuote('residential')}>
                Build My Quote
              </button>
              <a className="hero__phone-link" href="tel:+447849040289">or call +44 7849 040289</a>
            </div>
            <div className="hero__trust">
              <span>Instant pricing</span>
              <span className="trust-divider">|</span>
              <span>No hidden fees</span>
              <span className="trust-divider">|</span>
              <span>Book online in minutes</span>
              <span className="trust-divider">|</span>
              <span>Fully insured</span>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="section" id="services">
          <div className="container">
            <div className="section__header">
              <span className="section__tag">Our Services</span>
              <h2 className="section__title">Professional Cleaning for Every Space</h2>
              <p className="section__subtitle">From regular domestic cleans to full commercial contracts, we cover homes, offices, short-term lets, and everything in between.</p>
            </div>
            <div className="services-grid">
              
              <div className="service-card io-reveal">
                <div className="service-card__image-wrap">
                  <img src={IMAGES.residential} alt="Residential Cleaning" className="service-card__image" />
                </div>
                <div className="service-card__content">
                  <h3 className="service-card__title">Residential Cleaning</h3>
                  <p className="service-card__description">Weekly, fortnightly or one-off cleans for houses, flats and apartments. A consistent cleaner assigned to your property.</p>
                  <span className="service-card__price">From £65</span>
                </div>
              </div>

              <div className="service-card io-reveal">
                <div className="service-card__image-wrap">
                  <img src={IMAGES.commercial} alt="Commercial Cleaning" className="service-card__image" />
                </div>
                <div className="service-card__content">
                  <h3 className="service-card__title">Commercial Cleaning</h3>
                  <p className="service-card__description">Offices, medical centres, gyms, salons, and retail spaces. Tailored contracts with dedicated account management.</p>
                  <span className="service-card__price">Tailored quote</span>
                </div>
              </div>

              <div className="service-card io-reveal">
                <div className="service-card__image-wrap">
                  <img src={IMAGES.deep} alt="Deep Cleaning" className="service-card__image" />
                </div>
                <div className="service-card__content">
                  <h3 className="service-card__title">Deep Cleaning</h3>
                  <p className="service-card__description">End of tenancy, move-in and move-out cleans, and seasonal deep cleans. Every surface, appliance, and fixture thoroughly cleaned.</p>
                  <span className="service-card__price">From £85</span>
                </div>
              </div>

              <div className="service-card io-reveal">
                <div className="service-card__image-wrap">
                  <img src={IMAGES.airbnb} alt="Airbnb Turnovers" className="service-card__image" />
                </div>
                <div className="service-card__content">
                  <h3 className="service-card__title">Airbnb & Short Lets</h3>
                  <p className="service-card__description">Guest-ready turnover cleans with linen changes available. Flexible scheduling with same-day availability for hosts and property managers.</p>
                  <span className="service-card__price">From £55</span>
                </div>
              </div>

            </div>

            <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
              <button className="btn btn--primary btn--large" onClick={() => handleStartQuote('residential')}>
                Build Your Quote Now
              </button>
              <p style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: 'var(--color-gray-600)' }}>
                Takes 60 seconds — instant price, no obligation
              </p>
            </div>
          </div>
        </section>

        {/* Why Us Section */}
        <section className="section section--dark" id="standard">
          <div className="container">
            <div className="section__header">
              <span className="section__tag">Why Clenz Clean</span>
              <h2 className="section__title">Higher Standards, Without Compromise</h2>
              <p className="section__subtitle">We're not just another cleaning company. Here's what sets us apart from the rest.</p>
            </div>
            <div className="features-grid">
              <div className="feature io-reveal">
                <div className="feature__icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                </div>
                <h3 className="feature__title">Fully Insured & Vetted</h3>
                <p className="feature__description">Every cleaner is DBS-checked, fully insured, and trained to our exacting standards before they enter your property.</p>
              </div>
              <div className="feature io-reveal">
                <div className="feature__icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                </div>
                <h3 className="feature__title">Flexible Scheduling</h3>
                <p className="feature__description">Mornings, evenings, and weekends — we work around your schedule. Same-week bookings are often available.</p>
              </div>
              <div className="feature io-reveal">
                <div className="feature__icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                </div>
                <h3 className="feature__title">Transparent Pricing</h3>
                <p className="feature__description">See your exact price before you book. No hidden extras, no surprise charges — what you see is what you pay.</p>
              </div>
              <div className="feature io-reveal">
                <div className="feature__icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                </div>
                <h3 className="feature__title">Consistent Quality</h3>
                <p className="feature__description">The same cleaner assigned to every visit for regular bookings. Digital checklists ensure nothing is overlooked.</p>
              </div>
              <div className="feature io-reveal">
                <div className="feature__icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                </div>
                <h3 className="feature__title">Eco-Friendly Products</h3>
                <p className="feature__description">Professional-grade, environmentally responsible cleaning products. Safe for families, pets, and the environment.</p>
              </div>
              <div className="feature io-reveal">
                <div className="feature__icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
                </div>
                <h3 className="feature__title">Easy Online Booking</h3>
                <p className="feature__description">Build your quote, select a date, and confirm your booking — all in one seamless flow. No paperwork required.</p>
              </div>
              <div className="feature io-reveal">
                <div className="feature__icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg>
                </div>
                <h3 className="feature__title">No Long-Term Contracts</h3>
                <p className="feature__description">Residential cleans carry zero commitment. Commercial clients receive flexible terms that adapt to your business needs.</p>
              </div>
              <div className="feature io-reveal">
                <div className="feature__icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                </div>
                <h3 className="feature__title">Local & Always Available</h3>
                <p className="feature__description">Based in East London, covering Essex and Kent. We're nearby when you need us, with fast response times.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Reviews */}
        <section className="section section--light" id="reviews">
          <div className="container">
            <div className="section__header">
              <span className="section__tag">Client Feedback</span>
              <h2 className="section__title">Trusted by Homes & Businesses</h2>
              <p className="section__subtitle">We're proud to deliver reliable, high-standard cleaning services across East London, Essex and Kent.</p>
            </div>
            <div className="reviews-grid">
              <div className="review-card io-reveal">
                <div className="review-card__stars">★★★★★</div>
                <blockquote className="review-card__quote">"Excellent service. Reliable, punctual and always leaves the pharmacy spotless. Very professional team."</blockquote>
                <div className="review-card__author">
                  <strong>Aktive Pharmacy</strong>
                  <span>Harold Wood</span>
                </div>
              </div>
              <div className="review-card io-reveal">
                <div className="review-card__stars">★★★★★</div>
                <blockquote className="review-card__quote">"Needed a last-minute deep clean before guests arrived. They saved the day. Highly recommend."</blockquote>
                <div className="review-card__author">
                  <strong>Edward A.</strong>
                  <span>Romford</span>
                </div>
              </div>
              <div className="review-card io-reveal">
                <div className="review-card__stars">★★★★★</div>
                <blockquote className="review-card__quote">"Our weekly home clean has been brilliant. Friendly staff and very thorough."</blockquote>
                <div className="review-card__author">
                  <strong>Sarah M.</strong>
                  <span>Hornchurch</span>
                </div>
              </div>
              <div className="review-card io-reveal">
                <div className="review-card__stars">★★★★★</div>
                <blockquote className="review-card__quote">"I run an Airbnb and needed dependable turnover cleans. Great communication and always guest-ready."</blockquote>
                <div className="review-card__author">
                  <strong>Daniel R.</strong>
                  <span>Stratford Airbnb Host</span>
                </div>
              </div>
              <div className="review-card io-reveal">
                <div className="review-card__stars">★★★★★</div>
                <blockquote className="review-card__quote">"Very impressed with the bathroom and kitchen clean. Everything sparkling."</blockquote>
                <div className="review-card__author">
                  <strong>Nadia K.</strong>
                  <span>Ilford</span>
                </div>
              </div>
              <div className="review-card io-reveal">
                <div className="review-card__stars">★★★★★</div>
                <blockquote className="review-card__quote">"Great office cleaning service. Consistent, trustworthy and flexible with timings."</blockquote>
                <div className="review-card__author">
                  <strong>Direct Services Ltd</strong>
                  <span>East London</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Coverage Areas */}
        <section className="section" id="areas">
          <div className="container">
            <div className="section__header">
              <span className="section__tag">Coverage</span>
              <h2 className="section__title">Serving East London, Essex & Kent</h2>
              <p className="section__subtitle">Based in East London, we cover homes, offices and commercial premises across the region.</p>
            </div>
            <div className="areas-grid">
              <span className="area-tag">Newham</span>
              <span className="area-tag">Stratford</span>
              <span className="area-tag">East Ham</span>
              <span className="area-tag">Forest Gate</span>
              <span className="area-tag">Barking</span>
              <span className="area-tag">Dagenham</span>
              <span className="area-tag">Ilford</span>
              <span className="area-tag">Redbridge</span>
              <span className="area-tag">Romford</span>
              <span className="area-tag">Hornchurch</span>
              <span className="area-tag">Upminster</span>
              <span className="area-tag">Havering</span>
              <span className="area-tag">Brentwood</span>
              <span className="area-tag">Chelmsford</span>
              <span className="area-tag">Basildon</span>
              <span className="area-tag">Southend-on-Sea</span>
              <span className="area-tag">Colchester</span>
              <span className="area-tag">Dartford</span>
              <span className="area-tag">Gravesend</span>
              <span className="area-tag">Maidstone</span>
              <span className="area-tag">Gillingham</span>
              <span className="area-tag">Chatham</span>
              <span className="area-tag">Rochester</span>
              <span className="area-tag">Sevenoaks</span>
              <span className="area-tag">Tonbridge</span>
              <span className="area-tag">Tunbridge Wells</span>
              <span className="area-tag">Ashford</span>
              <span className="area-tag">Canterbury</span>
              <span className="area-tag">Folkestone</span>
              <span className="area-tag">Dover</span>
              <span className="area-tag">Margate</span>
              <span className="area-tag">Ramsgate</span>
              <span className="area-tag">Broadstairs</span>
              <span className="area-tag">Herne Bay</span>
              <span className="area-tag">Whitstable</span>
            </div>
            <p className="areas__more" style={{ textAlign: 'center', marginTop: '1.5rem', color: 'var(--color-gray-600)' }}>
              Don't see your area? Contact us and we'll confirm availability.
            </p>
          </div>
        </section>

        {/* Quote Wizard */}
        <QuoteWizard preselectedService={selectedService} onServiceChange={setSelectedService} />

        {/* Final CTA */}
        <section className="section cta-section">
          <div className="container">
            <div className="cta-content">
              <h2 className="cta-title">Ready for a Cleaner Space?</h2>
              <p className="cta-text">Build your quote in 60 seconds. Instant pricing, no obligation, and a spotless result guaranteed.</p>
              <div className="cta-actions">
                <button className="btn btn--primary btn--large" onClick={() => handleStartQuote('residential')}>
                  Get My Free Quote
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer__grid">
            <div className="footer__brand">
              <img src={logoUrl} alt="Clenz Clean Logo" className="footer__logo-img" />
              <p className="footer__description">Professional cleaning across East London, Essex and Kent. Instant quotes, trusted cleaners, exceptional results.</p>
              <p className="footer__contact">
                <a href="mailto:hello@clenzclean.com">hello@clenzclean.com</a><br />
                <a href="tel:+447849040289">+44 7849 040289</a>
              </p>
            </div>
            <div className="footer__links">
              <h4>Services</h4>
              <a href="#services">Residential Cleaning</a>
              <a href="#services">Commercial Cleaning</a>
              <a href="#services">Deep Cleaning</a>
              <a href="#services">Airbnb Turnovers</a>
            </div>
            <div className="footer__links">
              <h4>Areas</h4>
              <a href="#areas">East London</a>
              <a href="#areas">Essex</a>
              <a href="#areas">Kent</a>
            </div>
            <div className="footer__links">
              <h4>Follow Us</h4>
              <a 
                href="https://www.instagram.com/clenzclean" 
                target="_blank" 
                rel="noopener noreferrer"
                className="footer__social-link"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                </svg>
                @clenzclean
              </a>
            </div>
          </div>
          <div className="footer__bottom">
            <p>© {new Date().getFullYear()} Clenz Clean. All rights reserved.</p>
            <p className="footer__legal">Professional cleaning across East London, Essex & Kent</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;